import React, { useState, useEffect } from 'react';
import { API_URL } from "../config";
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaEye, FaEyeSlash, FaLock, FaSpinner, FaCheck, FaTimes } from 'react-icons/fa';

function SetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Password validation rules
  const rules = [
    { label: 'At least 8 characters', test: (p) => p.length >= 8 },
    { label: '1 uppercase letter', test: (p) => /[A-Z]/.test(p) },
    { label: '1 lowercase letter', test: (p) => /[a-z]/.test(p) },
    { label: '1 number', test: (p) => /\d/.test(p) },
    { label: '1 special character (@$!%*?&)', test: (p) => /[@$!%*?&]/.test(p) },
    { label: 'Passwords match', test: (p) => p === confirmPassword && p.length > 0 }
  ];

  const allRulesMet = rules.every(rule => rule.test(password));
  const passwordsMatch = password === confirmPassword && password.length > 0;



  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!allRulesMet) {
      setError('Please meet all password requirements');
      return;
    }

    if (!passwordsMatch) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');

      if (!token || !user.id) {
        setError('Session expired. Please use the magic link from your email again.');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/users/set-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user_id: user.id,
          new_password: password
        })
      });

      const data = await response.json();

      if (data.success) {
        toast.success('✅ Password set successfully! You can now log in.');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
      } else {
        setError(data.error || 'Failed to set password. Please try again.');
      }
    } catch (err) {
      setError('Failed to connect to server. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const checkItem = (passed) => ({
    color: passed ? '#00A86B' : '#D1D5DB',
    fontSize: '12px'
  });

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#F8FAFC',
      padding: '20px'
    }}>
      <div style={{
        maxWidth: '420px',
        width: '100%',
        background: 'white',
        borderRadius: '16px',
        padding: '40px',
        boxShadow: '0 4px 24px rgba(0,0,0,0.05)'
      }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <div style={{
            display: 'inline-block',
            background: '#0A1929',
            padding: '12px 28px',
            borderRadius: '12px'
          }}>
            <span style={{ color: '#00A86B', fontWeight: 700, fontSize: '22px' }}>ONE</span>
            <span style={{ color: 'white', fontWeight: 700, fontSize: '22px' }}>VIEW</span>
          </div>
        </div>

        <h2 style={{
          color: '#0A1929',
          fontSize: '22px',
          fontWeight: 700,
          margin: '0 0 8px',
          textAlign: 'center'
        }}>Set Your Password</h2>
        <p style={{
          color: '#64748B',
          fontSize: '14px',
          textAlign: 'center',
          margin: '0 0 24px',
          lineHeight: 1.5
        }}>
          Welcome! Please set a password for your account.
        </p>

        {error && (
          <div style={{
            background: '#FEF2F2',
            border: '1px solid #FECACA',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '20px',
            color: '#DC2626',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: 600,
              color: '#374151',
              marginBottom: '6px'
            }}>
              New Password
            </label>
            <div style={{ position: 'relative' }}>
              <FaLock style={{
                position: 'absolute',
                left: '12px',
                top: '18px',
                color: '#9CA3AF'
              }} />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create a strong password"
                style={{
                  width: '100%',
                  padding: '12px 12px 12px 36px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '12px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: '#9CA3AF'
                }}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          {/* Password Requirements Checklist */}
          <div style={{
            background: '#F9FAFB',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px'
          }}>
            <p style={{ fontSize: '12px', fontWeight: 600, color: '#6B7280', margin: '0 0 8px' }}>
              Password must contain:
            </p>
            {rules.slice(0, 5).map((rule, index) => {
              const passed = rule.test(password);
              return (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '3px 0'
                }}>
                  {passed ? (
                    <FaCheck size={11} color="#00A86B" />
                  ) : (
                    <FaTimes size={11} color="#D1D5DB" />
                  )}
                  <span style={{
                    fontSize: '12px',
                    color: passed ? '#00A86B' : '#9CA3AF',
                    textDecoration: passed ? 'none' : 'none'
                  }}>
                    {rule.label}
                  </span>
                </div>
              );
            })}
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              fontSize: '13px',
              fontWeight: 600,
              color: '#374151',
              marginBottom: '6px'
            }}>
              Confirm Password
            </label>
            <div style={{ position: 'relative' }}>
              <FaLock style={{
                position: 'absolute',
                left: '12px',
                top: '18px',
                color: '#9CA3AF'
              }} />
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repeat your password"
                style={{
                  width: '100%',
                  padding: '12px 12px 12px 36px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '8px',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>
            {confirmPassword.length > 0 && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                marginTop: '6px'
              }}>
                {passwordsMatch ? (
                  <>
                    <FaCheck size={11} color="#00A86B" />
                    <span style={{ fontSize: '12px', color: '#00A86B' }}>Passwords match</span>
                  </>
                ) : (
                  <>
                    <FaTimes size={11} color="#EF4444" />
                    <span style={{ fontSize: '12px', color: '#EF4444' }}>Passwords do not match</span>
                  </>
                )}
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={loading || !allRulesMet || !passwordsMatch}
            style={{
              width: '100%',
              padding: '14px',
              background: (loading || !allRulesMet || !passwordsMatch) ? '#94A3B8' : '#00A86B',
              color: 'white',
              border: 'none',
              borderRadius: '40px',
              fontSize: '16px',
              fontWeight: 600,
              cursor: (loading || !allRulesMet || !passwordsMatch) ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            {loading ? (
              <>
                <FaSpinner className="animate-spin" /> Setting Password...
              </>
            ) : (
              'Set Password'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

export default SetPassword;